import { useRef, useState } from "react";
import api from "../lib/api";
import { AuthImage } from "./AuthImage";
import { Upload, X, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";

/**
 * Single image uploader. Returns fileId via onChange.
 * Props:
 *   value: current file id (string|null)
 *   onChange: (fileId|null) => void
 *   aspect: "video" | "square"
 */
export const ImageUploader = ({ value, onChange, aspect = "video", testid = "image-uploader" }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Datei ist zu groß (max. 10 MB)");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Nur Bilder erlaubt");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.id);
    } catch (e) {
      const status = e.response?.status;
      if (status === 413) toast.error("Datei ist zu groß (max. 10 MB)");
      else if (status === 415) toast.error("Nur Bilder erlaubt");
      else toast.error("Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const ratio = aspect === "video" ? "aspect-[16/9]" : "aspect-square";

  return (
    <div data-testid={testid}>
      <input
        type="file"
        accept="image/*"
        ref={inputRef}
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />

      {value ? (
        <div className={`relative ${ratio} w-full rounded-2xl overflow-hidden bg-bone group`}>
          <AuthImage fileId={value} size="medium" className="w-full h-full object-cover" alt="cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="absolute bottom-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="px-3 py-1.5 text-xs rounded-full bg-white/90 text-ink hover:bg-white"
              data-testid={`${testid}-replace`}
            >
              Ändern
            </button>
            <button
              type="button"
              onClick={() => onChange(null)}
              className="p-1.5 rounded-full bg-white/90 text-ink hover:bg-white"
              data-testid={`${testid}-remove`}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className={`${ratio} w-full rounded-2xl border border-dashed border-border bg-bone/40 hover:bg-bone hover:border-ink/30 flex flex-col items-center justify-center gap-2 text-text_secondary transition-colors`}
          data-testid={`${testid}-trigger`}
        >
          {uploading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              <span className="text-xs">Lädt …</span>
            </>
          ) : (
            <>
              <ImagePlus size={20} strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-[0.2em]">Coverbild hinzufügen</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

/**
 * Multi-image uploader. value is string[] of file ids.
 */
export const MultiImageUploader = ({ value = [], onChange, testid = "multi-uploader" }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const uploadMany = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newIds = [];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} ist zu groß (max. 10 MB)`);
        continue;
      }
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name}: nur Bilder erlaubt`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newIds.push(data.id);
      } catch (e) {
        const status = e.response?.status;
        if (status === 413) toast.error(`${file.name}: zu groß (max. 10 MB)`);
        else if (status === 415) toast.error(`${file.name}: nur Bilder erlaubt`);
        else toast.error(`${file.name} fehlgeschlagen`);
      }
    }
    if (newIds.length) onChange([...(value || []), ...newIds]);
    setUploading(false);
  };

  const remove = (idx) => {
    const next = [...value];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div data-testid={testid}>
      <input
        type="file"
        accept="image/*"
        multiple
        ref={inputRef}
        className="hidden"
        onChange={(e) => uploadMany(Array.from(e.target.files || []))}
      />
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {value.map((fileId, idx) => (
          <div key={fileId} className="relative aspect-square rounded-xl overflow-hidden bg-bone group" data-testid={`${testid}-item-${idx}`}>
            <AuthImage fileId={fileId} size="thumb" className="w-full h-full object-cover" alt={`image-${idx}`} />
            <button
              type="button"
              onClick={() => remove(idx)}
              className="absolute top-1.5 right-1.5 p-1 rounded-full bg-white/90 text-ink opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`${testid}-remove-${idx}`}
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="aspect-square rounded-xl border border-dashed border-border bg-bone/40 hover:bg-bone hover:border-ink/30 flex flex-col items-center justify-center gap-1 text-text_secondary transition-colors"
          data-testid={`${testid}-trigger`}
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          <span className="text-[10px] uppercase tracking-wider">{uploading ? "Lädt" : "Hinzufügen"}</span>
        </button>
      </div>
    </div>
  );
};

export default ImageUploader;
